/**
 * The sizes a widget is offered at on a page laid out in twelve columns: its share of a row, and how many
 * rows tall. A person picks one of these by name, never a width.
 */
export const WIDGET_SIZES = {
    small: { label: "Small", w: 3, h: 2 },
    medium: { label: "Medium", w: 4, h: 2 },
    wide: { label: "Wide", w: 6, h: 2 },
    large: { label: "Large", w: 6, h: 4 },
};
// A single figure reads at a quarter of a row; a chart needs a third; a list is never taller than its rows.
const FIGURE = ["small", "medium", "wide"];
const CHART = ["medium", "wide", "large"];
const WIDE_CHART = ["wide", "medium", "large"];
const LIST = ["medium", "wide"];
export const WIDGET_CATALOGUE = [
    { type: "metric", name: "Figure", answers: "One number that matters: a count, a total or an average of a channel's records.", sizes: FIGURE },
    { type: "time_series", name: "Trend", answers: "How one measure moves over time, by day, week, month or year.", sizes: CHART },
    { type: "breakdown", name: "Breakdown", answers: "How many records fall into each category of a field.", sizes: LIST },
    { type: "bar", name: "Bars", answers: "A total or an average for each category of a field, as bars or as shares of a whole.", sizes: LIST },
    { type: "grouped_bar", name: "Grouped bars", answers: "One measure compared across two categories at once, side by side or stacked.", sizes: WIDE_CHART },
    { type: "stacked_bar", name: "Stacked trend", answers: "A measure over time, split into the parts that make up each period.", sizes: WIDE_CHART },
    { type: "waterfall", name: "Waterfall", answers: "How a starting figure becomes an ending one, change by change.", sizes: WIDE_CHART },
    { type: "record_list", name: "Records", answers: "The records themselves as a short table: the latest, the largest or the most urgent.", sizes: ["wide"] },
    { type: "insight_list", name: "Insights", answers: "What stands out, in words: saved notes, or notes kept up to date from the charts beside them.", sizes: LIST },
    { type: "risk_list", name: "Risks", answers: "What could go wrong, in words: saved notes, or notes kept up to date from the charts beside them.", sizes: LIST },
    // A channel's Overview only: not a dashboard's.
    { type: "progress", name: "Progress", answers: "How far one measure has got towards a target: collected against a budget, done against planned.", sizes: FIGURE },
    { type: "goal_rings", name: "Goal rings", answers: "Up to six goals at once, each a measure against its own target, as rings that close when met.", sizes: LIST },
    { type: "countdown", name: "Countdown", answers: "Days until a date that matters, with the key date after it.", sizes: ["small", "medium"] },
    { type: "status_list", name: "Status list", answers: "Things with a status each, shown in a tone: invoices overdue, due, paid; workstreams on track or at risk.", sizes: LIST },
    { type: "streak", name: "Streak", answers: "How busy each day of the last few weeks was, as a map of days.", sizes: LIST },
    { type: "ranked_bars", name: "Ranked bars", answers: "The top few categories of a field by a measure, largest first.", sizes: LIST },
];
const byType = new Map(WIDGET_CATALOGUE.map((entry) => [entry.type, entry]));
/** The catalogue's entry for a widget type. */
export function widgetCatalogueEntry(type) {
    const entry = byType.get(type);
    if (!entry)
        throw new Error(`No catalogue entry for widget type ${type}`);
    return entry;
}
/**
 * The sizes a widget of this type is offered at, the one it is first given first. A type this release does
 * not know is treated as a chart: never Small.
 */
export function widgetSizes(type) {
    return byType.get(type)?.sizes ?? CHART;
}
/**
 * Whether a type shows one figure (a figure, progress, a countdown): the only kind offered Small. A page
 * gives one figure less room than a chart, and sets it in the middle of a tile taller than itself.
 */
export function isSingleFigureWidget(type) {
    return widgetSizes(type).includes("small");
}
/** Whether a type draws a plot on a scale (a trend, bars by series, a bridge): the only kind offered Large, and the kind whose height a page chooses. */
export function isPlottedWidget(type) {
    return widgetSizes(type).includes("large");
}
